import { Chip, Typography, useTheme } from "@mui/material";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowEmpty
} from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { Inv_InventoryDateLabelSettings } from "~/config/clientConfig";
import useFlag from "~/config/flags";
import { useCantInductIneligibilityByPutawayTaskId } from "~/hooks/useCantInductReasons";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { PutAwayTaskSummaryDto } from "~/types/api";

import { AutostorePutawaySearch } from "./AutostorePutawaySearch";
import NotesRow from "./NotesRow";
import {
  selectIsPreviousBinFlow,
  setSearchData
} from "./autostorePutaway.slice";
import { useGetPutawayTasks } from "./useGetPutawayTasks";

type Props = {
  inv_inventoryDateLabel: Inv_InventoryDateLabelSettings;
  handleSelectRow: (row: PutAwayTaskSummaryDto) => void;
  pageLimit: 5 | 7 | 10;
  selectedRow?: PutAwayTaskSummaryDto | undefined;
  hasPutawayTableV2EnhancedFlag?: boolean;
};

export const PutawayTasksTable = (props: Props) => {
  const {
    pageLimit,
    selectedRow,
    handleSelectRow,
    hasPutawayTableV2EnhancedFlag = false
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const { enableColorChanges } = useFlag();
  const trackedPageName = useMixpanelPageName();
  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);

  const searchData = useAppSelector(
    (state) => state.autostorePutaway.searchData
  );

  const {
    putawayTasks,
    putawayTasksLoading,
    putawayTasksTotalCount,
    error: putawayTasksError
  } = useGetPutawayTasks(pageLimit, searchData);
  const ineligibleByPutawayTaskId =
    useCantInductIneligibilityByPutawayTaskId(putawayTasks);

  const { putaway_showScanFilters } = useAppSelector(selectServerConfig);

  const totalPageCount = putawayTasksTotalCount
    ? Math.ceil(putawayTasksTotalCount / pageLimit)
    : 0;

  const tableHeaderColNames = [
    t("product"),
    t("orderedQuantityAbbr"),
    t("remainingQuantityAbbr"),
    t("purchase order number abbr"),
    t("notes")
  ];

  const removeFilterCb = (barcode: string) => {
    dispatch(
      setSearchData({
        ...searchData,
        scannedBarcodes: searchData.scannedBarcodes.filter(
          (bc) => bc !== barcode
        )
      })
    );
  };

  const isSanitizeTargetBin = selectedRow?.product?.isSanitizeTargetBin;

  const showFilters =
    putaway_showScanFilters && !!searchData.scannedBarcodes.length;

  return (
    <ASTableV2
      gridTemplateColumns={`repeat(${tableHeaderColNames.length}, auto)`}
      gridTemplateRows={`${showFilters ? "50px 50px" : "50px"} repeat(${pageLimit}, 1fr)`}
      isLoading={putawayTasksLoading}
      pagination={{
        page: searchData.offset,
        pageCount: putawayTasksError ? 1 : totalPageCount,
        setPage: (p) => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Change Page",
            eventProperties: {
              Page: p
            }
          });
          dispatch(setSearchData({ ...searchData, offset: p }));
        },
        disabled: isPreviousBinFlow
      }}
      searchComponent={
        hasPutawayTableV2EnhancedFlag ? <AutostorePutawaySearch /> : undefined
      }
    >
      <ASTableV2Header>
        <ASTableV2Row>
          {tableHeaderColNames.map((h) => (
            <ASTableV2Cell key={h}>
              <Typography variant="tableBody">{h}</Typography>
            </ASTableV2Cell>
          ))}
        </ASTableV2Row>
        {showFilters && (
          <ASTableV2Row>
            <ASTableV2Cell sx={{ gridColumn: "1 / -1" }}>
              {searchData.scannedBarcodes.map((filter) => (
                <Chip
                  key={filter}
                  label={filter}
                  color="1"
                  onDelete={
                    removeFilterCb ? () => removeFilterCb(filter) : undefined
                  }
                  sx={{ mr: 2, fontSize: 14 }}
                />
              ))}
            </ASTableV2Cell>
          </ASTableV2Row>
        )}
      </ASTableV2Header>
      <ASTableV2Body>
        {!putawayTasksLoading && !putawayTasks.length && (
          <ASTableV2RowEmpty gridRow={`${showFilters ? 3 : 2} / last-line`}>
            {t("no results")}
          </ASTableV2RowEmpty>
        )}
        {!putawayTasksError &&
          !putawayTasksLoading &&
          putawayTasks.map((task) => {
            const selected = task.putAwayTaskId === selectedRow?.putAwayTaskId;
            const ineligible =
              ineligibleByPutawayTaskId.get(task.putAwayTaskId) ?? false;
            const safetyStateSx =
              enableColorChanges &&
              selected &&
              !ineligible &&
              isSanitizeTargetBin
                ? {
                    "&& > td": {
                      backgroundColor:
                        palette.autostoreDesignSystem.colour.tertiary
                          .asPurple100,
                      borderColor: palette.autostoreDesignSystem.mts.organic
                    }
                  }
                : enableColorChanges &&
                    selected &&
                    !ineligible &&
                    task.product.isFoodSafetyItem
                  ? {
                      "&& > td": {
                        backgroundColor:
                          palette.autostoreDesignSystem.surface.static.warning,
                        borderColor:
                          palette.autostoreDesignSystem.border.onStandard
                            .statusWarning
                      }
                    }
                  : undefined;

            return (
              <ASTableV2Row
                key={task.putAwayTaskId}
                onClick={() => handleSelectRow(task)}
                selected={selected}
                disabled={isPreviousBinFlow}
                warning={task?.product.isFoodSafetyItem}
                sanitize={isSanitizeTargetBin}
                sx={safetyStateSx}
                ineligible={ineligible}
              >
                <ASTableV2Cell sx={{ wordBreak: "break-word" }}>
                  <Typography variant="tableBody">
                    {task.product.productName}
                  </Typography>
                </ASTableV2Cell>
                <ASTableV2Cell sx={{ wordBreak: "break-word" }}>
                  <Typography variant="tableBody">
                    {task.orderedQuantity.value.toString()}{" "}
                    {task.orderedQuantity.units}
                  </Typography>
                </ASTableV2Cell>
                <ASTableV2Cell sx={{ wordBreak: "break-word" }}>
                  <Typography variant="tableBody">
                    {task.remaining.value.toString()} {task.remaining.units}
                  </Typography>
                </ASTableV2Cell>
                <ASTableV2Cell sx={{ wordBreak: "break-word" }}>
                  <Typography variant="tableBody">
                    {task.purchaseOrderNumber || ""}
                  </Typography>
                </ASTableV2Cell>

                <ASTableV2Cell sx={{ wordBreak: "break-word" }}>
                  <NotesRow
                    receivingContainerCode={task.receivingContainerCode}
                  />
                </ASTableV2Cell>
              </ASTableV2Row>
            );
          })}
      </ASTableV2Body>
    </ASTableV2>
  );
};
